import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { createRoot } from 'react-dom/client';
import {
	createBrowserRouter,
	Navigate,
	RouterProvider, // Needed for nested layout
} from 'react-router-dom';

import App from './components/App/App'; // App will be our layout component
import Contacte from './components/App/Contacte';
import Inici from './components/App/Inici';
import Login from './components/App/Login';
import Rocacorba from './components/App/Rocacorba';
import AvisForm from './components/Avis/AvisForm';
import AdminDashboard from './components/Admin/AdminDashboard';
// Import page/route components
import Calendar from './components/Calendar/Calendar';
import CalendarUnitatSelection from './components/Calendar/CalendarUnitatSelection';
import EventForm from './components/Calendar/EventForm';
import ComissioForm from './components/Comissio/ComissioForm';
import { AuthProvider } from './components/Context/AuthContext';
import { UnitatsProvider } from './components/Context/UnitatsContext';
import AlbumEditForm from './components/Gallery/AlbumEditForm';
import AlbumForm from './components/Gallery/AlbumForm';
import ClassificationOverview from './components/Gallery/ClassificationOverview';
import YearSuggestionsOverview from './components/Gallery/YearSuggestionsOverview';
import GalleryContainer from './components/Gallery/GalleryContainer';
import Post from './components/Post/Post';
import PostForm from './components/Post/PostForm';
import Posts from './components/Post/Posts';
import FacturesRoute from './components/Shared/FacturesRoute';
import Factures from './components/Factures/Factures';
import AdminRoute from './components/Shared/AdminRoute';
import ProtectedRoute from './components/Shared/ProtectedRoute';
// import Unitat from './components/Unitat/Unitat'; // Uncomment if needed
// import Unitats from './components/Unitat/Unitats'; // Uncomment if needed

const rootElement = document.getElementById('root');

if (!rootElement) {
	throw new Error('No root element found');
}

const root = createRoot(rootElement);
const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 1000 * 60 * 5, // 5 minutes
		},
	},
});

// Define routes using the object syntax
const router = createBrowserRouter([
	{
		path: '/',
		element: <App />, // App component acts as the layout
		children: [
			{ path: 'inici', element: <Inici /> },
			{ path: 'login', element: <Login /> },
			{ path: 'avisos/crear', element: <AvisForm mode="create" /> },
			{ path: 'avisos/:id/editar', element: <AvisForm mode="edit" /> },
			{
				path: 'comissions/crear',
				element: <ComissioForm mode="create" />,
			},
			{
				path: 'comissions/:id/editar',
				element: <ComissioForm mode="edit" />,
			},
			{
				path: 'admin/dashboard',
				element: (
					<AdminRoute>
						<AdminDashboard />
					</AdminRoute>
				),
			},
			// { path: 'unitats/:unitat', element: <Unitat /> }, // Uncomment if needed
			// { path: 'unitats', element: <Unitats /> }, // Uncomment if needed
			{ path: 'posts', element: <Posts /> },
			{
				path: 'posts/crear/:unitat?',
				element: <PostForm mode="create" />,
			},
			{ path: 'posts/:id/editar', element: <PostForm mode="edit" /> },
			{ path: 'posts/:id', element: <Post /> },
			{
				path: 'galeria/classificar/classificacions',
				element: (
					<ProtectedRoute>
						<ClassificationOverview />
					</ProtectedRoute>
				),
			},
			{
				path: 'galeria/anys',
				element: (
					<ProtectedRoute>
						<YearSuggestionsOverview />
					</ProtectedRoute>
				),
			},
			{
				path: 'galeria',
				element: (
					<ProtectedRoute>
						<GalleryContainer />
					</ProtectedRoute>
				),
			},
			{
				path: 'galeria/:categoryId',
				element: (
					<ProtectedRoute>
						<GalleryContainer />
					</ProtectedRoute>
				),
			},
			{
				path: 'galeria/:categoryId/:albumId',
				element: (
					<ProtectedRoute>
						<GalleryContainer />
					</ProtectedRoute>
				),
			},
			{
				path: 'galeria/albums/:category/nou',
				element: (
					<ProtectedRoute>
						<AlbumForm />
					</ProtectedRoute>
				),
			},
			{
				path: 'galeria/albums/:id/editar',
				element: (
					<ProtectedRoute>
						<AlbumEditForm />
					</ProtectedRoute>
				),
			},
			{ path: 'calendari', element: <CalendarUnitatSelection /> },
			{ path: 'calendari/veure', element: <Calendar /> },
			{ path: 'calendari/crear', element: <EventForm mode="create" /> },
			// { path: 'calendari/crear/:unitat', element: <EventForm mode="create" /> }, // Uncomment if needed
			{
				path: 'calendari/editar/:id',
				element: <EventForm mode="edit" />,
			},
			{
				path: 'factures/:schoolYear?',
				element: (
					<FacturesRoute>
						<Factures />
					</FacturesRoute>
				),
			},
			{ path: 'contacte', element: <Contacte /> },
			{ path: 'rocacorba', element: <Rocacorba /> },
			// Redirect root and any unmatched paths to /inici
			{ index: true, element: <Navigate to="/inici" replace /> },
			{ path: '*', element: <Navigate to="/inici" replace /> },
		],
	},
]);

root.render(
	<React.StrictMode>
		<QueryClientProvider client={queryClient}>
			<UnitatsProvider>
				<AuthProvider>
					{/* Use RouterProvider */}
					<RouterProvider router={router} />
				</AuthProvider>
			</UnitatsProvider>
			{/* <ReactQueryDevtools initialIsOpen={false} position="bottom" /> */}
		</QueryClientProvider>
	</React.StrictMode>
);
